/**
 * PhotoGallery — cover + up to 4 additional product photos with
 * drag-to-reorder. The cover slot uses the existing `ImageUploader`
 * (file picker → WebP → FormData). Each gallery slot accepts BOTH a
 * direct file picker (also WebP-optimized) and a free-text URL — a
 * freshly-picked file takes precedence; typing a URL clears the
 * picked file.
 *
 * The component is fully controlled: parent owns `cover` (string URL
 * or null) + `coverFile` (Blob when freshly picked) + `gallery`
 * (array of stable-id rows; each row may also carry a freshly-picked
 * `file` Blob). The parent serializes `gallery` rows into the
 * multipart body: rows with a `file` get an empty-url placeholder
 * and the Blob is appended under the `gallery_file` field; rows
 * with just a URL pass through unchanged.
 *
 * Drag-to-reorder uses native HTML5 DnD. No external library. Sufficient
 * for 4 items; touch UX is out of scope (admins typically on desktop).
 *
 * Total cap: 1 cover + 4 gallery = 5 photos per product, enforced
 * server-side via the schema's `.max(4)` on `gallery` and surfaced
 * in the UI by hiding the "+ Add" button once full.
 */
import { useEffect, useRef, useState, type DragEvent } from 'react'
import { GripVertical, Plus, Upload, X } from 'lucide-react'
import { toast } from 'sonner'

import { ImageUploader } from './ImageUploader'
import { Input } from './Input'
import { Button } from './Button'
import { cn } from '../lib/cn'
import { optimizeImage } from '../lib/imageOptimize'

export interface GalleryPhoto {
  /** Stable React key for drag-reorder. The server's row.id is used
   *  on existing photos; new rows get a client-side UUID. */
  id: string
  url: string
  /** Server uses 0-based sortOrder. Always equals the array index on
   *  save, but the field is kept so we can pass through existing
   *  rows unchanged. */
  sortOrder: number
  /** When the user picked a file in this slot, this holds the
   *  WebP-optimized Blob ready for upload. The parent inspects
   *  this on submit and appends the Blob to the FormData under
   *  the `gallery_file` field, sending an empty `url` in the
   *  gallery array as a placeholder. */
  file?: Blob
}

export interface PhotoGalleryProps {
  /** Cover image — pass the existing string/Blob pattern so the
   *  parent's submit logic stays the same as the original editor. */
  cover: string | null
  coverFile: Blob | string | null
  onCoverChange: (next: Blob | string | null) => void

  /** Additional photos (max 4). */
  gallery: GalleryPhoto[]
  onGalleryChange: (next: GalleryPhoto[]) => void
}

const MAX_GALLERY = 4
const MAX_FILE_BYTES = 5 * 1024 * 1024

/** Cheap client-side UUID. crypto.randomUUID exists in modern browsers
 *  and Node 19+; the fallback keeps older targets working. */
function newId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return `g-${Math.random().toString(36).slice(2, 11)}-${Date.now()}`
}

function isBlobUrl(value: string): boolean {
  return value.startsWith('blob:')
}

export function PhotoGallery({
  cover,
  coverFile,
  onCoverChange,
  gallery,
  onGalleryChange,
}: PhotoGalleryProps) {
  /** Track which row is being dragged. HTML5 DnD uses dataTransfer
   *  which doesn't carry a clean identifier; a ref on the index is
   *  the simplest cross-browser approach. */
  const [dragIndex, setDragIndex] = useState<number | null>(null)
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)

  /* The hidden <input type="file"> ref for each gallery row. We
     index by row id so that the right row's file picker opens
     regardless of how many rows there are. */
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({})

  /* Revoke any blob: URLs we created for previews on unmount so
     the browser can release the underlying image data. */
  useEffect(() => {
    return () => {
      for (const photo of gallery) {
        if (photo.file && isBlobUrl(photo.url)) {
          URL.revokeObjectURL(photo.url)
        }
      }
    }
    // Run only on unmount; we intentionally don't track gallery
    // changes here to avoid revoking URLs out from under a still-
    // mounted row.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function addPhoto() {
    if (gallery.length >= MAX_GALLERY) return
    onGalleryChange([...gallery, { id: newId(), url: '', sortOrder: gallery.length }])
  }

  function updatePhoto(id: string, patch: Partial<GalleryPhoto>) {
    onGalleryChange(gallery.map((p) => (p.id === id ? { ...p, ...patch } : p)))
  }

  function removePhoto(id: string) {
    // Free the preview blob before discarding the row.
    const photo = gallery.find((p) => p.id === id)
    if (photo?.file && isBlobUrl(photo.url)) {
      URL.revokeObjectURL(photo.url)
    }
    delete fileInputRefs.current[id]
    onGalleryChange(gallery.filter((p) => p.id !== id))
  }

  function reorder(srcIdx: number, dstIdx: number) {
    if (srcIdx === dstIdx) return
    const next = [...gallery]
    const [moved] = next.splice(srcIdx, 1)
    next.splice(dstIdx, 0, moved)
    // Reassign sortOrder by index — the server uses these values.
    onGalleryChange(next.map((p, i) => ({ ...p, sortOrder: i })))
  }

  async function pickFile(id: string, file: File) {
    if (file.size > MAX_FILE_BYTES) {
      toast.error(`File too large (max 5 MB).`)
      return
    }
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file.')
      return
    }
    try {
      const blob = await optimizeImage(file)
      // Revoke any previous preview for this row first.
      const previous = gallery.find((p) => p.id === id)
      if (previous?.file && isBlobUrl(previous.url)) {
        URL.revokeObjectURL(previous.url)
      }
      const objectUrl = URL.createObjectURL(blob)
      updatePhoto(id, { url: objectUrl, file: blob })
    } catch (err) {
      toast.error('Failed to process image', {
        description: (err as Error)?.message ?? 'Unknown error',
      })
    }
  }

  function onUrlEdit(id: string, nextUrl: string) {
    // Typing in the URL field supersedes a freshly-picked file. We
    // revoke the preview URL and clear the file so it won't be
    // uploaded on submit.
    const photo = gallery.find((p) => p.id === id)
    if (photo?.file && isBlobUrl(photo.url)) {
      URL.revokeObjectURL(photo.url)
    }
    updatePhoto(id, { url: nextUrl, file: undefined })
  }

  /* ── Drag handlers (native HTML5 DnD) ─────────────────────── */

  function onDragStart(e: DragEvent<HTMLElement>, idx: number) {
    setDragIndex(idx)
    e.dataTransfer.effectAllowed = 'move'
    // Some browsers require setData to actually fire drag events.
    e.dataTransfer.setData('text/plain', String(idx))
  }

  function onDragOver(e: DragEvent<HTMLElement>, idx: number) {
    e.preventDefault() // allows drop
    e.dataTransfer.dropEffect = 'move'
    setDragOverIndex(idx)
  }

  function onDragLeave() {
    setDragOverIndex(null)
  }

  function onDrop(e: DragEvent<HTMLElement>, idx: number) {
    e.preventDefault()
    if (dragIndex == null) return
    reorder(dragIndex, idx)
    setDragIndex(null)
    setDragOverIndex(null)
  }

  function onDragEnd() {
    setDragIndex(null)
    setDragOverIndex(null)
  }

  const slotsRemaining = MAX_GALLERY - gallery.length
  const totalPhotos = (cover || coverFile ? 1 : 0) + gallery.length

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-base font-semibold text-ink">Photos</h2>
        <span className="text-xs text-slatey">
          {totalPhotos} of 5 photos — 1 cover + up to 4 gallery
        </span>
      </div>

      {/* Cover slot — keeps using the existing ImageUploader
          (file picker → WebP optimization → FormData) */}
      <div className="rounded-md border border-line p-4 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium uppercase tracking-wider text-slatey">
            Cover
          </span>
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-forest/10 text-forest font-medium">
            Primary
          </span>
        </div>
        <ImageUploader
          value={cover}
          onChange={(next) => {
            // PhotoGallery manages cover via the parent's three-state
            // contract: Blob = freshly picked, string = unchanged URL,
            // null = explicitly cleared.
            if (next === null) {
              onCoverChange(null)
            } else {
              onCoverChange(next)
            }
          }}
          label="Cover image"
        />
      </div>

      {/* Gallery slots */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium uppercase tracking-wider text-slatey">
            Gallery
          </span>
          {slotsRemaining > 0 ? (
            <Button type="button" variant="outline" size="sm" onClick={addPhoto}>
              <Plus className="size-3.5" /> Add photo
            </Button>
          ) : (
            <span className="text-xs text-slatey">Maximum reached</span>
          )}
        </div>

        {gallery.length === 0 ? (
          <p className="text-xs text-slatey italic">
            No gallery photos. Add up to 4 to show alongside the cover.
          </p>
        ) : (
          <ul className="space-y-2">
            {gallery.map((photo, idx) => (
              <li
                key={photo.id}
                draggable
                onDragStart={(e) => onDragStart(e, idx)}
                onDragOver={(e) => onDragOver(e, idx)}
                onDragLeave={onDragLeave}
                onDrop={(e) => onDrop(e, idx)}
                onDragEnd={onDragEnd}
                className={cn(
                  'flex items-center gap-2 rounded-md border border-line bg-white p-2 transition',
                  dragOverIndex === idx && dragIndex !== idx && 'border-forest bg-forest/5',
                  dragIndex === idx && 'opacity-50',
                )}
              >
                <GripVertical
                  className="size-4 text-slatey cursor-grab shrink-0"
                  aria-label="Drag to reorder"
                />
                <div className="size-12 shrink-0 rounded-md border border-line bg-ivory overflow-hidden flex items-center justify-center">
                  {photo.url ? (
                    <img
                      src={photo.url}
                      alt={`Gallery ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-[10px] text-slatey">No URL</span>
                  )}
                </div>
                <Input
                  value={photo.url}
                  onChange={(e) => onUrlEdit(photo.id, e.target.value)}
                  placeholder="https://…/photo.jpg"
                  className="flex-1"
                />
                {/* Hidden file input triggered by the upload icon button.
                    Using a ref instead of a label-with-input nesting
                    keeps the click target visually consistent with the
                    adjacent remove button. */}
                <input
                  ref={(el) => {
                    fileInputRefs.current[photo.id] = el
                  }}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0]
                    if (f) pickFile(photo.id, f)
                    // Clear the input value so picking the same file
                    // twice in a row still fires onChange.
                    e.target.value = ''
                  }}
                />
                <button
                  type="button"
                  onClick={() => fileInputRefs.current[photo.id]?.click()}
                  className="size-8 shrink-0 rounded-md text-slatey hover:text-forest hover:bg-forest/10 inline-flex items-center justify-center"
                  aria-label="Upload image"
                  title="Upload image"
                >
                  <Upload className="size-4" />
                </button>
                <button
                  type="button"
                  onClick={() => removePhoto(photo.id)}
                  className="size-8 shrink-0 rounded-md text-slatey hover:text-danger hover:bg-danger/10 inline-flex items-center justify-center"
                  aria-label="Remove photo"
                  title="Remove"
                >
                  <X className="size-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}